import styled from "styled-components";
import { Link } from "react-router-dom";
import { StaggerReveal } from "../../components/ui/Motion";
import { GlassCard } from "../../components/ui/Glass";

export const PageHeader = styled.div`
  position: relative;
  padding-top: calc(4rem + 60px);
  padding-bottom: ${({ theme }) => theme.spacing["3xl"]};
  background: ${({ theme }) => theme.colors.navy};
  text-align: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        circle at 50% 0%,
        rgba(112, 141, 171, 0.18) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 15% 100%,
        rgba(201, 169, 110, 0.07) 0%,
        transparent 45%
      );
    pointer-events: none;
  }

  & > * {
    position: relative;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-top: calc(4rem + 50px);
  }
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.5rem, 5vw, ${({ theme }) => theme.fontSizes["6xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
`;

export const HeaderSub = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: rgba(255, 255, 255, 0.55);
  max-width: 520px;
  margin: 0 auto;
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

export const Grid = styled(StaggerReveal)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const BrandCard = styled(GlassCard).attrs({ $variant: "light" })`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  box-shadow: ${({ theme }) => theme.shadows.md};
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    background: ${({ theme }) => theme.colors.white};
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow:
      0 16px 40px rgba(59, 84, 109, 0.18),
      0 4px 12px rgba(112, 141, 171, 0.12);
  }
`;

export const LogoArea = styled.div`
  aspect-ratio: 16 / 9;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primaryDark} 0%,
    ${({ theme }) => theme.colors.navy} 100%
  );
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding: ${({ theme }) => theme.spacing["2xl"]};
  overflow: hidden;

  img,
  svg {
    display: block;
    width: auto;
    height: auto;
    max-width: 60%;
    max-height: 50%;
    transition:
      transform 0.6s cubic-bezier(0.16, 1, 0.3, 1),
      opacity ${({ theme }) => theme.transitions.normal};
  }

  svg {
    width: 60%;
    filter: brightness(0) invert(1);
    opacity: 0.92;
  }

  img {
    object-fit: contain;
    /* Match the SVG white treatment for monochrome PNG logos */
    filter: brightness(0) invert(1);
    opacity: 0.92;
  }

  ${BrandCard}:hover & img,
  ${BrandCard}:hover & svg {
    transform: scale(1.04);
  }

  ${BrandCard}:hover & svg {
    opacity: 1;
  }
`;

export const LogoText = styled.span`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

export const CardBody = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const BrandName = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 3.2vw, ${({ theme }) => theme.fontSizes["4xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
  line-height: 1.05;
  margin-bottom: 4px;

  /* Premium gradient fill: deep navy fading into gold */
  color: ${({ theme }) => theme.colors.navy};
  background: linear-gradient(
    105deg,
    ${({ theme }) => theme.colors.navy} 0%,
    ${({ theme }) => theme.colors.primaryDark} 42%,
    ${({ theme }) => theme.colors.gold} 100%
  );
  background-size: 200% auto;
  background-position: 0% center;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: background-position 0.6s cubic-bezier(0.16, 1, 0.3, 1);

  /* On hover the gradient slides so the gold sweeps across the name */
  ${BrandCard}:hover & {
    background-position: 100% center;
  }
`;

/* Origin sits below the name as a small, muted caps label */
export const BrandOrigin = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: 11px;
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const BrandDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex: 1;

  /* Clamp the description to 4 lines, then ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const EmptyState = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing["3xl"]} ${theme.spacing["2xl"]}`};

  background: ${({ theme }) => theme.colors.white};
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.xl};

  svg {
    width: 28px;
    height: 28px;
    color: ${({ theme }) => theme.colors.primary};
    padding: 22px;
    box-sizing: content-box;
    background: ${({ theme }) => theme.colors.pearl};
    border-radius: ${({ theme }) => theme.borderRadius.full};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    box-shadow: 0 0 0 8px rgba(112, 141, 171, 0.05);
  }

  p {
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: ${({ theme }) => theme.fontSizes["2xl"]};
    font-weight: ${({ theme }) => theme.fontWeights.regular};
    color: ${({ theme }) => theme.colors.textPrimary};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }

  span {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    color: ${({ theme }) => theme.colors.textLight};
    max-width: 360px;
    line-height: ${({ theme }) => theme.lineHeights.relaxed};
  }
`;

export const ViewLink = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  &::after {
    content: "→";
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  &:hover::after {
    transform: translateX(4px);
  }
`;

export const ViewLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin-top: auto;
  transition: color ${({ theme }) => theme.transitions.fast};

  &::after {
    content: "→";
    display: inline-block;
    transition: transform ${({ theme }) => theme.transitions.normal};
  }

  ${BrandCard}:hover & {
    color: ${({ theme }) => theme.colors.primaryDark};
  }

  ${BrandCard}:hover &::after {
    transform: translateX(6px);
  }
`;
